import React from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  Stack,
  Divider,
  Avatar,
  Button,
  Container,
  useTheme,
  useMediaQuery,
  CardMedia
} from "@mui/material";
import {
  Terrain,
  Backpack,
  StarRate,
  HealthAndSafety,
  LocalFireDepartment,
  Hiking,
  Landscape,
  CheckCircle,
  Forest
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import TourCard from "../Components/Cards/TourCard";

const TrekkingTours = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // ================= TREK & CAMP PACKAGES =================
  // NOTE: I replaced the cartoon images in this array with realistic photos for the actual booking cards.
  // The cartoons are used in the design sections below as requested.
  const tourData = [
    {
      id: "tr1",
      title: "Triund Trek & Camping",
      places: "McLeod Ganj • Triund • Snowline",
      price: "₹ 3,999/-",
      image:
        "https://www.dpolohotels.com/wp-content/uploads/2024/11/Tips-for-an-Unforgettable-Triund-Trek-Experience-1.jpg",
      rating: 4.8
    },
    {
      id: "tr2",
      title: "Kedarkantha Winter Trek",
      places: "Sankri • Juda Ka Talab • Kedarkantha",
      price: "₹ 6,499/-",
      image:
        "https://himalayandaredevils.com/storage/uploads/6717f4a2375e9.webp",
      rating: 4.9
    },
    {
      id: "tr3",
      title: "Kasol Riverside Camping",
      places: "Kasol • Chalal • Parvati River",
      price: "₹ 2,999/-",
      image:
        "https://www.hireacamp.com/api/external/image?name=uploads/ee8294235e4143e7a49979d2390b845c.jpeg&size=3840x3840&q=75",
      rating: 4.6
    },
    {
      id: "tr4",
      title: "Chandratal Lake Camping",
      places: "Batal • Chandratal • Spiti",
      price: "₹ 5,999/-",
      image:
        "https://www.transformingtravels.com/wp-content/uploads/2022/01/Camping-near-Chandratal.jpg",
      rating: 4.9
    }
  ];

  // ================= ENRICH =================
  const enrichTour = (tour, index) => {
    const durations = ["2D / 1N", "5D / 4N", "2D / 1N", "4D / 3N"];
    const tags = ["Beginner Friendly", "Bestseller", "Weekend Escape", "Premium"];

    return {
      ...tour,
      duration: durations[index % durations.length],
      tag: tags[index % tags.length],
      oldPrice:
        "₹ " +
        (parseInt(tour.price.replace(/[₹,/-]/g, "")) + 1500).toLocaleString() +
        "/-",
      discountEndsIn: "Limited Slots Available",
    };
  };

  return (
    <Box sx={{ backgroundColor: "#FAFAF9", minHeight: "100vh"}}>
      
      {/* HERO SECTION WITH CARTOON DESIGN ELEMENT */}
      <Box
        sx={{
          position: "relative",
          bgcolor: "#064E3B", // Deep Forest Green
          pt: 8,
          pb: { xs: 8, md: 12, },
           px: { xs: 0, md: 24,},
         
          overflow: "hidden",
          borderBottomRightRadius: { xs: 0, md: "100px" },
        }}
      >
        <Container maxWidth="xl">
          <Grid container alignItems="center" spacing={6}>
            <Grid item xs={12} md={6} sx={{ zIndex: 2 }}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Chip
                  icon={<Forest />}
                  label="Adventure Experiences"
                  sx={{
                    bgcolor: "rgba(255,255,255,0.15)",
                    color: "#bef264",
                    fontWeight: 600,
                    mb: 3,
                    backdropFilter: "blur(4px)",
                    border: "1px solid rgba(255,255,255,0.2)"
                  }}
                />
                <Typography
                  variant="h2"
                  sx={{
                    fontFamily: '"Playfair Display", serif',
                    fontWeight: 800,
                    color: "white",
                    fontSize: { xs: "2.5rem", md: "4rem" },
                    mb: 3,
                    lineHeight: 1.1
                  }}
                >
                  Trekking & <br /> Camping
                </Typography>
                <Typography
                  variant="h6"
                  sx={{ color: "rgba(255,255,255,0.8)", mb: 4, maxWidth: "500px", lineHeight: 1.6 }}
                >
                  Trek through alpine meadows, sleep under starlit skies, sit beside warm campfires, and wake up to mountain silence.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => document.getElementById("packages").scrollIntoView({ behavior: "smooth" })}
                  sx={{
                    backgroundColor: "#EAB308", // Warm Gold
                    color: "#064E3B",
                    fontWeight: 700,
                    px: 4,
                    py: 1.5,
                    borderRadius: 50,
                    boxShadow: "0 10px 25px rgba(0,0,0,0.3)",
                    "&:hover": { backgroundColor: "#FDE047", transform: "translateY(-3px)" },
                  }}
                >
                  View Packages
                </Button>
              </motion.div>
            </Grid>

            {/* DECORATIVE CARTOON IMAGE */}
            <Grid item xs={12} md={6} sx={{ position: "relative", display: "flex", justifyContent: "center" }}>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                sx={{
                  width: { xs: "80%", md: "90%" },
                  maxWidth: "600px",
                  position: "relative",
                  zIndex: 1
                }}
              >
                {/* Using the Interracial Couple Hike Image as a Masked Circle */}
                <Box
                  sx={{
                    width: "100%",
                    aspectRatio: "1/1",
                    borderRadius: "50%",
                    border: "8px solid rgba(255,255,255,0.1)",
                    overflow: "hidden",
                    backgroundColor: "#065f46"
                  }}
                >
                   <img 
                    src="https://img.freepik.com/free-vector/interracial-couple-going-hike_1308-134871.jpg?semt=ais_hybrid&w=740&q=80" 
                    alt="Couple Hiking"
                    style={{ width: "100%", height: "100%", objectFit: "cover", mixBlendMode: "normal" }}
                   />
                </Box>
                
                {/* Floating Badge */}
                <Box
                  sx={{
                    position: "absolute",
                    bottom: "40px",
                    right: { xs: "0px", md: "-20px" },
                    backgroundColor: "white",
                    padding: "1.5rem",
                    borderRadius: "20px",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
                    maxWidth: "200px",
                    textAlign: "center"
                  }}
                >
                  <Typography variant="h4" fontWeight={800} color="#064E3B">500+</Typography>
                  <Typography variant="body2" color="#666">Treks Completed</Typography>
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* VIBE SECTION (Using Campfire Image) */}
      <Box sx={{ py: 8, px: { xs: 2, md: 6 }, backgroundColor: "#FFF7ED" }}>
        <Container maxWidth="lg">
          <Grid container alignItems="center" spacing={6}>
             <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    borderRadius: "24px",
                    overflow: "hidden",
                    boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                    border: "4px solid #fff"
                  }}
                >
                  {/* Using Campfire Image */}
                  <CardMedia
                    component="img"
                    image="https://static.vecteezy.com/system/resources/thumbnails/049/143/168/small/a-lone-figure-sits-by-a-campfire-under-a-full-moon-in-a-mountain-lake-setting-photo.jpeg"
                    alt="Campfire"
                    sx={{ height: 400, width: "100%" }}
                  />
                </Box>
             </Grid>
             <Grid item xs={12} md={6}>
               <Typography variant="overline" color="#EA580C" fontWeight={700} letterSpacing={1.5}>
                 THE ATMOSPHERE
               </Typography>
               <Typography variant="h3" sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, my: 2, color: "#1C1917" }}>
                 Authentic Wilderness
               </Typography>
               <Typography variant="body1" sx={{ color: "#44403C", lineHeight: 1.8, mb: 4 }}>
                 Our camping isn't about luxury hotels in a forest. It's about the crackle of a fire, the reflection of the moon on a silent lake, and the feeling of being truly small in the grand mountains.
               </Typography>
               <Stack direction="row" gap={2}>
                 <Chip icon={<StarRate />} label="Bonfire Nights" sx={{ backgroundColor: "white", color: "#EA580C", fontWeight: 600, border: "1px solid #EEDDCC" }} />
                 <Chip icon={<Forest />} label="Forest Camping" sx={{ backgroundColor: "white", color: "#16A34A", fontWeight: 600, border: "1px solid #EEDDCC" }} />
               </Stack>
             </Grid>
          </Grid>
        </Container>
      </Box>

      {/* TOUR CARDS */}
      <Box id="packages" sx={{ py: 8, backgroundColor: "#fff" }}>
        <Container maxWidth="xl">
          <Typography
            variant="h3"
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontWeight: 700,
              mb: 5,
              textAlign: "center",
              color: "#111827"
            }}
          >
            Popular Treks & Camps
          </Typography>
          <Grid container spacing={4} justifyContent="center">
            {tourData.map((tour, index) => (
              <Grid item xs={12} sm={6} md={3} key={tour.id}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -10 }}
                >
                  <TourCard
                    tour={tour}
                    index={index}
                    onViewDetails={() =>
                      navigate("/tourdetails", {
                        state: enrichTour(tour, index),
                      })
                    }
                    onEnquire={() => navigate("/booking")}
                  />
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* WHY TREK & CAMP (Using Hiking Man Illustration) */}
      <Box sx={{ py: 10, backgroundColor: "#FAFAF9" }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontWeight: 700,
              mb: 8,
              textAlign: "center",
              color: "#111827"
            }}
          >
            Why Trek With Us?
          </Typography>
          <Grid container spacing={4}>
            {/* Feature 1 */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 4, height: "100%", borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
                <Stack spacing={2} alignItems="flex-start">
                   <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: "#ECFCCB", color: "#3F6212" }}>
                     <Hiking />
                   </Box>
                  <Typography variant="h6" fontWeight={700}>Curated Routes</Typography>
                  <Typography variant="body2" color="text.secondary" lineHeight={1.7}>
                    Safe trails picked for scenic beauty and safety. From beginner walks to steep climbs.
                  </Typography>
                </Stack>
              </Paper>
            </Grid>
             {/* Feature 2 - With Cartoon Image Design */}
            <Grid item xs={12} md={4}>
               <Paper sx={{ p: 4, height: "100%", borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.05)", position: "relative", overflow: "hidden" }}>
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: "-20px",
                      right: "-20px",
                      width: "120px",
                      height: "120px",
                      opacity: 0.2
                    }}
                  >
                     {/* Using Hiking Man Illustration as watermark */}
                     <img src="https://freedesignfile.com/upload/2019/12/Hiking_man_icon_cartoon_character_sketch_vector_12873014.jpg" alt="Hiker" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                  </Box>
                  <Stack spacing={2} alignItems="flex-start">
                     <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: "#FFEDD5", color: "#C2410C" }}>
                       <LocalFireDepartment />
                     </Box>
                    <Typography variant="h6" fontWeight={700}>Authentic Camping</Typography>
                    <Typography variant="body2" color="text.secondary" lineHeight={1.7}>
                      Riverside, meadows, and clearings. No crowded tourist camps, only real nature.
                    </Typography>
                  </Stack>
                </Paper>
            </Grid>
            {/* Feature 3 */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 4, height: "100%", borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
                <Stack spacing={2} alignItems="flex-start">
                   <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: "#DCFCE7", color: "#15803D" }}>
                     <Terrain />
                   </Box>
                  <Typography variant="h6" fontWeight={700}>Expert Guides</Typography>
                  <Typography variant="body2" color="text.secondary" lineHeight={1.7}>
                    Locals who know the terrain, weather patterns, and handle emergencies efficiently.
                  </Typography>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* TREKKER'S GUIDE (Checklist Style) */}
      <Box sx={{ py: 10, px: { xs: 2, md: 6 }, backgroundColor: "#065F46", color: "white" }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontWeight: 700,
              mb: 6,
              textAlign: "center",
              color: "white"
            }}
          >
            Trekker’s Essential Guide
          </Typography>

          <Grid container spacing={4}>
            {/* Packing */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 4, borderRadius: 3, bgcolor: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", height: "100%" }}>
                <Stack spacing={2} alignItems="center" mb={3}>
                  <Avatar sx={{ bgcolor: "#EAB308", width: 56, height: 56 }}>
                    <Backpack />
                  </Avatar>
                  <Typography variant="h5" fontWeight={700}>Packing Essentials</Typography>
                </Stack>
                <Stack spacing={1.5}>
                  {["Trekking Shoes (Must)", "Thermal Layers", "Rain Jacket/Poncho", "Sunscreen & Sunglasses", "Power Bank", "Personal Meds"].map((item, idx) => (
                    <Stack key={idx} direction="row" spacing={2} alignItems="center">
                      <CheckCircle sx={{ color: "#4ADE80", fontSize: "1.2rem" }} />
                      <Typography variant="body1" sx={{ color: "#E2E8F0" }}>{item}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Grid>

            {/* Safety */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 4, borderRadius: 3, bgcolor: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", height: "100%" }}>
                <Stack spacing={2} alignItems="center" mb={3}>
                  <Avatar sx={{ bgcolor: "#F87171", width: 56, height: 56 }}>
                    <HealthAndSafety />
                  </Avatar>
                  <Typography variant="h5" fontWeight={700}>Safety Guidelines</Typography>
                </Stack>
                <Stack spacing={1.5}>
                  {["Follow Guide Instructions", "Hydrate Regularly", "Acclimatize Slowly", "Carry ID Proof", "No Littering (Leave No Trace)", "Inform about Medical History"].map((item, idx) => (
                    <Stack key={idx} direction="row" spacing={2} alignItems="center">
                      <CheckCircle sx={{ color: "#4ADE80", fontSize: "1.2rem" }} />
                      <Typography variant="body1" sx={{ color: "#E2E8F0" }}>{item}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA */}
      <Box
        sx={{
          py: 12,
          backgroundImage: "linear-gradient(135deg, #064E3B 0%, #047857 100%)",
          color: "white",
          textAlign: "center"
        }}
      >
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="h3"
              sx={{
                fontFamily: '"Playfair Display", serif',
                fontWeight: 800,
                mb: 3,
                fontSize: { xs: "2rem", md: "3rem" }
              }}
            >
              Ready for Your Next Adventure?
            </Typography>
            <Typography sx={{ maxWidth: "800px", mx: "auto", lineHeight: 1.8, fontSize: "1.1rem", opacity: 0.9, mb: 5 }}>
              Whether it’s a weekend camp or a high-altitude trek, we help you experience the mountains the right way.
            </Typography>
            <Button
              size="large"
              variant="contained"
              sx={{
                backgroundColor: "#EAB308",
                color: "#064E3B",
                fontWeight: 700,
                px: 6,
                py: 1.5,
                fontSize: "1.1rem",
                borderRadius: 50,
                boxShadow: "0 4px 14px rgba(0,0,0,0.2)",
                "&:hover": { backgroundColor: "#FDE047" },
              }}
              onClick={() => navigate("/booking")}
            >
              Enquire Now
            </Button>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default TrekkingTours;